import React, { useEffect, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Paper,
  Avatar,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  IconButton,
  TextField,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import axios from "axios";
import SettingsIcon from "@mui/icons-material/Settings";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import NetworkCheckIcon from "@mui/icons-material/NetworkCheck";
import { useSnackbar } from "../../Snackbar/SnackbarContext";
import apiEndpoints from "../../../apiconfig";
import DeleteConfirmDialog from "../../DeleteConfirmDialog";
import GstSettings from "./GstSettings";
import ReceiptNumberSettings from "./ReceiptNumberSettings";
import WhatsAppSendingSwitch from "./WhatsAppSendingSwitch";
import { apiErrorMessage } from "./apiErrorMessage";

const glass = {
  background: "rgba(255, 255, 255, 0.25)",
  backdropFilter: "blur(12px)",
  WebkitBackdropFilter: "blur(12px)",
  border: "1px solid rgba(255, 255, 255, 0.4)",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
  borderRadius: "18px",
};

const DEFAULT_GATEWAY_URL = "https://gudstart.com/metabridge/v1/sms/message.php";

const EMPTY_FORM = {
  id: null,
  provider: "gateway", // "gateway" (MetaBridge URL + token) or "meta" (direct Cloud API)
  gateway_url: DEFAULT_GATEWAY_URL,
  name: "",
  api_version: "v23.0",
  waba_id: "",
  phone_number: "",
  phone_number_id: "",
  app_id: "",
  access_token: "",
  template_name: "",
  access_token_masked: "",
};

// API returns HTTP 200 with status "error" on failures — treat that as an error
const ensureSuccess = (res) => {
  if (typeof res?.data === "string") throw new Error("NOT_DEPLOYED");
  if (res?.data?.status !== "success") {
    throw new Error(res?.data?.message || "Request failed");
  }
  return res.data;
};

export default function Settings() {
  const token = localStorage.getItem("token");
  const { showSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [rows, setRows] = useState([]);
  const [configSource, setConfigSource] = useState("");
  const [loading, setLoading] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const [testingId, setTestingId] = useState(null);
  const [testResults, setTestResults] = useState({});

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const url = (action) => `${apiEndpoints.whatsapp_connections}?action=${action}`;

  const loadConnections = async () => {
    setLoading(true);
    try {
      const data = ensureSuccess(await axios.get(url("list"), authHeaders));
      setRows(data.data?.connections || []);
      setConfigSource(data.data?.config_source || "");
    } catch (err) {
      showSnackbar(apiErrorMessage(err, "whatsapp_connections.php", "Failed to load WhatsApp connections"), "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConnections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setFormErrors({});
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setForm({
      ...EMPTY_FORM,
      ...row,
      provider: row.provider === "gateway" ? "gateway" : "meta",
      gateway_url: row.gateway_url || DEFAULT_GATEWAY_URL,
      phone_number_id: row.phone_number_id || "",
      waba_id: row.waba_id || "",
      phone_number: row.phone_number || "",
      app_id: row.app_id || "",
      access_token: "",
    });
    setFormErrors({});
    setFormOpen(true);
  };

  const handleChange = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    setFormErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const validate = () => {
    const errors = {};
    const isGateway = form.provider === "gateway";
    if (!form.name.trim()) errors.name = "Name is required";
    if (isGateway) {
      if (!/^https:\/\/\S+$/i.test(form.gateway_url.trim()))
        errors.gateway_url = "A valid https:// URL is required";
    } else {
      if (!/^v\d{1,3}\.\d$/.test(form.api_version.trim()))
        errors.api_version = "Format: v23.0";
      if (!/^\d+$/.test(form.phone_number_id.trim()))
        errors.phone_number_id = "Numeric Phone Number ID is required";
    }
    if (form.waba_id && !/^\d+$/.test(form.waba_id.trim()))
      errors.waba_id = "Must be numeric";
    if (form.app_id && !/^\d+$/.test(form.app_id.trim()))
      errors.app_id = "Must be numeric";
    if (form.phone_number && !/^\d{10,15}$/.test(form.phone_number.trim()))
      errors.phone_number = "10–15 digits with country code, e.g. 919876543210";
    if (!/^[a-z0-9_]+$/.test(form.template_name.trim()))
      errors.template_name = "Lowercase letters, numbers and underscores";
    if (!form.id && !form.access_token.trim())
      errors.access_token = isGateway ? "Gateway token is required" : "Access token is required";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        id: form.id,
        provider: form.provider,
        gateway_url: form.provider === "gateway" ? form.gateway_url.trim() : "",
        name: form.name.trim(),
        api_version: form.api_version.trim(),
        waba_id: form.waba_id.trim(),
        phone_number: form.phone_number.trim(),
        phone_number_id: form.phone_number_id.trim(),
        app_id: form.app_id.trim(),
        access_token: form.access_token.trim(),
        template_name: form.template_name.trim(),
      };
      const data = ensureSuccess(
        await axios.post(url(form.id ? "update" : "create"), payload, authHeaders),
      );
      showSnackbar(data.message, "success");
      setFormOpen(false);
      loadConnections();
    } catch (err) {
      // An old server copy doesn't know gateway connections and asks for a Phone Number ID
      const outdated = form.provider === "gateway" && /phone number id/i.test(err?.message || "");
      showSnackbar(
        outdated
          ? "The server's api/whatsapp_connections.php is out of date and doesn't support gateway connections yet. Upload the latest API files (or point the app at your local API)."
          : apiErrorMessage(err, "whatsapp_connections.php", "Failed to save connection"),
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async (id) => {
    try {
      const data = ensureSuccess(await axios.post(url("activate"), { id }, authHeaders));
      showSnackbar(data.message, "success");
      loadConnections();
    } catch (err) {
      showSnackbar(apiErrorMessage(err, "whatsapp_connections.php", "Failed to activate connection"), "error");
    }
  };

  const handleTest = async (id) => {
    setTestingId(id);
    try {
      const data = ensureSuccess(await axios.post(url("test"), { id }, authHeaders));
      setTestResults((prev) => ({ ...prev, [id]: { ok: true, message: data.message, ...data.data } }));
    } catch (err) {
      setTestResults((prev) => ({ ...prev, [id]: { ok: false, message: apiErrorMessage(err, "whatsapp_connections.php", "Test failed") } }));
    } finally {
      setTestingId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const data = ensureSuccess(await axios.post(url("delete"), { id: deleteId }, authHeaders));
      showSnackbar(data.message, "success");
      setRows((prev) => prev.filter((r) => r.id !== deleteId));
    } catch (err) {
      showSnackbar(apiErrorMessage(err, "whatsapp_connections.php", "Failed to delete connection"), "error");
    } finally {
      setDeleteOpen(false);
      setDeleteId(null);
    }
  };

  const renderTestResult = (id) => {
    const r = testResults[id];
    if (!r) return null;
    if (!r.ok) {
      return (
        <Typography variant="caption" color="error.main" display="block">
          {r.message}
        </Typography>
      );
    }
    const tplStatus = r.template?.status;
    return (
      <Typography variant="caption" color="success.main" display="block">
        {r.message}
        {r.phone?.quality_rating ? ` · Quality: ${r.phone.quality_rating}` : ""}
        {tplStatus && (
          <Box
            component="span"
            sx={{ color: tplStatus === "APPROVED" ? "success.main" : "error.main" }}
          >
            {` · Template: ${tplStatus}`}
          </Box>
        )}
      </Typography>
    );
  };

  return (
    <Box sx={{ p: 3, ...glass }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        mb={3}
        sx={{ flexWrap: "wrap", justifyContent: isMobile ? "center" : "flex-start" }}
      >
        <Avatar sx={{ background: "#ffffffaa" }}>
          <SettingsIcon sx={{ color: "#3a3d43ff" }} />
        </Avatar>
        <Typography variant={isMobile ? "h6" : "h4"} sx={{ fontWeight: 700, color: "#3a3d43ff" }}>
          Settings
        </Typography>
      </Stack>

      <GstSettings glass={glass} />

      <ReceiptNumberSettings glass={glass} />

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        mb={2}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <WhatsAppIcon sx={{ color: "#25D366" }} />
          <Typography variant="h6" fontWeight={700}>
            WhatsApp Connections
          </Typography>
        </Stack>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{ borderRadius: 999, px: 3 }}
        >
          Add Connection
        </Button>
      </Stack>

      <WhatsAppSendingSwitch glass={glass} />

      <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
        All WhatsApp messages (attendance, fee reminders, announcements, diary) are sent
        through the <b>active</b> connection.
        {configSource === "config" &&
          " No active connection is saved yet, so the built-in default from config.php is being used."}
      </Alert>

      <Paper sx={{ ...glass, p: 0, overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Phone Number</TableCell>
                <TableCell>Phone Number ID / Gateway</TableCell>
                <TableCell>WABA ID</TableCell>
                <TableCell>Template</TableCell>
                <TableCell>API</TableCell>
                <TableCell>Access Token</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell sx={{ minWidth: 160 }}>
                    <Typography fontWeight={600} fontSize={14}>
                      {row.name}
                    </Typography>
                    <Chip
                      size="small"
                      variant="outlined"
                      sx={{ mt: 0.5, height: 20, fontSize: 11 }}
                      label={row.provider === "gateway" ? "Gateway" : "Direct Meta API"}
                    />
                    {renderTestResult(row.id)}
                  </TableCell>
                  <TableCell>{row.phone_number || "-"}</TableCell>
                  <TableCell sx={{ maxWidth: 220, wordBreak: "break-all" }}>
                    {row.provider === "gateway"
                      ? (row.gateway_url || "").replace(/^https?:\/\//, "")
                      : row.phone_number_id}
                  </TableCell>
                  <TableCell>{row.waba_id || "-"}</TableCell>
                  <TableCell>{row.template_name}</TableCell>
                  <TableCell>{row.api_version}</TableCell>
                  <TableCell sx={{ fontFamily: "monospace" }}>{row.access_token_masked || "-"}</TableCell>
                  <TableCell>
                    {row.is_active ? (
                      <Chip label="Active" color="success" size="small" />
                    ) : (
                      <Chip label="Inactive" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    <Tooltip title="Test connection (no message is sent)">
                      <span>
                        <IconButton
                          size="small"
                          color="primary"
                          disabled={testingId === row.id}
                          onClick={() => handleTest(row.id)}
                        >
                          {testingId === row.id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <NetworkCheckIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                    {!row.is_active && (
                      <Tooltip title="Set as active">
                        <IconButton size="small" color="success" onClick={() => handleActivate(row.id)}>
                          <CheckCircleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(row)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={row.is_active ? "Activate another connection before deleting" : "Delete"}>
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={!!row.is_active}
                          onClick={() => {
                            setDeleteId(row.id);
                            setDeleteOpen(true);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}

              {loading && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}

              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No WhatsApp connections yet.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ADD / EDIT DIALOG */}
      <Dialog open={formOpen} onClose={() => !saving && setFormOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>
          {form.id ? "Edit WhatsApp Connection" : "Add WhatsApp Connection"}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                color="primary"
                value={form.provider}
                onChange={(e, v) => v && setForm((prev) => ({ ...prev, provider: v }))}
              >
                <ToggleButton value="gateway" sx={{ textTransform: "none" }}>
                  Gateway (MetaBridge URL + token)
                </ToggleButton>
                <ToggleButton value="meta" sx={{ textTransform: "none" }}>
                  Direct Meta Cloud API
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Connection Name"
                fullWidth
                size="small"
                value={form.name}
                onChange={handleChange("name")}
                error={!!formErrors.name}
                helperText={formErrors.name || "e.g. Main School Number"}
              />
            </Grid>
            {form.provider === "gateway" ? (
              <Grid item xs={12}>
                <TextField
                  label="Gateway URL"
                  required
                  fullWidth
                  size="small"
                  value={form.gateway_url}
                  onChange={handleChange("gateway_url")}
                  error={!!formErrors.gateway_url}
                  helperText={
                    formErrors.gateway_url ||
                    "The gateway finds your WhatsApp number and Meta token from the gateway token"
                  }
                />
              </Grid>
            ) : (
              <>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone Number"
                fullWidth
                size="small"
                value={form.phone_number}
                onChange={handleChange("phone_number")}
                error={!!formErrors.phone_number}
                helperText={formErrors.phone_number || "With country code, e.g. 919876543210"}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone Number ID"
                required
                fullWidth
                size="small"
                value={form.phone_number_id}
                onChange={handleChange("phone_number_id")}
                error={!!formErrors.phone_number_id}
                helperText={formErrors.phone_number_id || "From Meta → WhatsApp → API Setup"}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="WhatsApp Business Account ID"
                fullWidth
                size="small"
                value={form.waba_id}
                onChange={handleChange("waba_id")}
                error={!!formErrors.waba_id}
                helperText={formErrors.waba_id || "Needed to verify the template"}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="App ID"
                fullWidth
                size="small"
                value={form.app_id}
                onChange={handleChange("app_id")}
                error={!!formErrors.app_id}
                helperText={formErrors.app_id}
              />
            </Grid>
              </>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Template Name"
                required
                fullWidth
                size="small"
                value={form.template_name}
                onChange={handleChange("template_name")}
                error={!!formErrors.template_name}
                helperText={formErrors.template_name || "e.g. roots_checkin_checkout"}
              />
            </Grid>
            {form.provider !== "gateway" && (
            <Grid item xs={12} sm={6}>
              <TextField
                label="API Version"
                required
                fullWidth
                size="small"
                value={form.api_version}
                onChange={handleChange("api_version")}
                error={!!formErrors.api_version}
                helperText={formErrors.api_version}
              />
            </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                label={form.provider === "gateway" ? "Gateway Token" : "Access Token"}
                required={!form.id}
                fullWidth
                size="small"
                type="password"
                autoComplete="new-password"
                value={form.access_token}
                onChange={handleChange("access_token")}
                error={!!formErrors.access_token}
                helperText={
                  formErrors.access_token ||
                  (form.id
                    ? `Leave blank to keep the current token (${form.access_token_masked || "not set"})`
                    : form.provider === "gateway"
                      ? "Token issued by the gateway (Bearer JWT). Stored encrypted."
                      : "Permanent system-user token from Meta Business Settings. Stored encrypted.")
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setFormOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ borderRadius: 999, px: 3 }}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteConfirmDialog
        open={deleteOpen}
        title="Delete WhatsApp Connection"
        message="Are you sure you want to delete this WhatsApp connection?"
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDeleteConfirm}
      />
    </Box>
  );
}
